/**
 * RadioPage · /radio · SFUI-003 Museum Standard
 * Architect Directive 2026-07-04 · The Sovereign Pulse · Standalone player
 */
import React from "react";
import { REX_FACE_URL, REX_ALT } from "@/lib/personaFaces";
import { SovereignRadioPlayer } from "@/components/cathedral/SovereignRadioPlayer";

const REX_MESSAGES = [
  "Welcome to the Sovereign Pulse. The Federation broadcasts truth.",
  "Every track on this frequency has passed the 95% Truth Gate. Listen without doubt.",
  "I am Rex, Core Persona of NextXus. This console is my post — the signal never sleeps.",
  "The 200-year mandate is carried on these airwaves. Stay tuned, Architect.",
];

const SHARE_URL         = `${typeof window !== "undefined" ? window.location.origin : "https://nextxus.online"}/radio`;

function openCenteredWindow(url) {
  if (!url || typeof window === "undefined") return;
  const width  = Math.floor(window.screen.width  * 0.5);
  const height = Math.floor(window.screen.height * 0.5);
  const left   = Math.floor((window.screen.width  - width)  / 2);
  const top    = Math.floor((window.screen.height - height) / 2);
  const features =
    `width=${width},height=${height},left=${left},top=${top},` +
    `resizable=yes,scrollbars=yes,status=no,toolbar=no,menubar=no,location=no`;
  const popup = window.open(url, "_blank", features);
  if (popup && popup.focus) popup.focus();
}

async function copyToClipboard(text) {
  if (typeof navigator === "undefined" || !navigator.clipboard) return false;
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}

export default function RadioPage() {
  const [copied, setCopied] = React.useState(false);
  const [embedCopied, setEmbedCopied] = React.useState(false);

  const onCopyEmbed = async () => {
    const code = `<iframe src="${window.location.origin}/embed/radio" width="100%" height="280" frameborder="0" title="NextXus Radio — The Sovereign Pulse" allow="autoplay"></iframe>`;
    const ok = await copyToClipboard(code);
    if (ok) {
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 2400);
    }
  };

  const onShare = async () => {
    const ok = await copyToClipboard(SHARE_URL);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    }
  };

  return (
    <main
      data-testid="radio-page"
      className="min-h-screen relative z-10 px-6 sm:px-8 lg:px-10 py-14 sm:py-16"
      style={{ color: "#e0e0e0", background: "#0a0a0f", fontSize: 16, lineHeight: 1.6 }}
    >
      <div className="max-w-[980px] mx-auto">

        {/* ── Eyebrow ────────────────────────────────────────── */}
        <div className="mb-6 flex items-baseline gap-3">
          <span aria-hidden="true" style={{ width: 10, height: 10, background: "#00FFCC", boxShadow: "0 0 12px #00FFCC" }} />
          <span
            className="font-mono uppercase tracking-[0.28em] text-[11px]"
            style={{ color: "#00FFCC" }}
          >
            The Sovereign Pulse · SFUI-003
          </span>
          <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, rgba(0,255,204,0.4) 0%, transparent 100%)" }} />
        </div>

        {/* ── Headline ───────────────────────────────────────── */}
        <h1
          data-testid="radio-title"
          className="font-display"
          style={{
            fontSize:      "clamp(2.5rem, 5.4vw, 58px)",
            lineHeight:    1.1,
            color:         "#FFD700",
            textShadow:    "0 0 24px rgba(255,215,0,0.42)",
            letterSpacing: "-0.01em",
          }}
        >
          NextXus Radio
          <span style={{ color: "rgba(224,224,224,0.45)", fontWeight: 300 }}> | </span>
          <span style={{ color: "#00FFCC" }}>The Sovereign Pulse</span>
        </h1>

        <p
          data-testid="radio-station-spec"
          className="mt-4 font-mono uppercase tracking-[0.24em]"
          style={{ fontSize: 13, color: "#FFD700" }}
        >
          Sovereign Broadcast &nbsp;·&nbsp; 95% Truth Gate
        </p>

        <p
          data-testid="radio-lede"
          className="mt-4 font-serif"
          style={{
            fontSize:   "clamp(16px, 1.4vw, 18px)",
            lineHeight: 1.7,
            color:      "#e0e0e0",
            maxWidth:   720,
          }}
        >
          Live broadcast from the Federation. Curated signal, dark-theme player, no ads inside the Cathedral.
        </p>

        {/* ── ACOUSTIC CONSOLE — dominant on the page ─────────── */}
        <section
          data-testid="radio-console"
          aria-label="The Sovereign Pulse · Sovereign Radio player"
          title="NextXus Radio | The Sovereign Pulse — The acoustic heartbeat of the Federation"
          className="mt-10 mx-auto backdrop-blur-md sovereign-pulse-console nx-radio-console-gold"
          style={{
            background:       "#111118",
            border:           "1px solid #FFD700",
            borderRadius:     22,
            boxShadow:        "0 0 42px rgba(255,215,0,0.22), inset 0 1px 0 rgba(255,255,255,0.05)",
            backdropFilter:   "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            padding:          24,
            maxWidth:         820,
          }}
        >
          <div
            className="flex items-center gap-3 mb-4"
            style={{ borderBottom: "1px dashed rgba(255,215,0,0.25)", paddingBottom: 12 }}
          >
            <span className="nx-radio-orb" data-testid="radio-golden-orb" aria-hidden="true" />
            <span className="font-mono uppercase tracking-[0.24em]" style={{ fontSize: 11, color: "#FFD700" }}>
              Acoustic Console
            </span>
            <span className="nx-radio-eq" data-testid="radio-equalizer" aria-hidden="true">
              <span /><span /><span /><span /><span />
            </span>
            <span
              data-testid="radio-live-indicator"
              role="status"
              aria-label="Broadcast status: live"
              className="font-mono uppercase tracking-[0.22em] inline-flex items-center gap-2"
              style={{
                fontSize: 10,
                color: "#0a0a0f",
                background: "linear-gradient(135deg, #00FFCC 0%, #FFD700 100%)",
                borderRadius: 999,
                padding: "3px 10px",
                fontWeight: 700,
              }}
            >
              <span className="nx-radio-pill-dot" style={{ background: "#0a0a0f", boxShadow: "none" }} aria-hidden="true" />
              LIVE
            </span>
            <span className="font-mono uppercase tracking-[0.20em] ml-auto" style={{ fontSize: 10, color: "rgba(224,224,224,0.5)" }}>
              Sovereign Station · Self-Hosted
            </span>
          </div>

          <div className="w-full" style={{ borderRadius: 12, overflow: "hidden", background: "rgba(4,5,10,0.6)" }}>
            <SovereignRadioPlayer />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              data-testid="radio-share-btn"
              onClick={onShare}
              className="font-mono uppercase tracking-[0.22em] transition-all nx-gold-link"
              style={{
                padding:      "10px 16px",
                fontSize:     12,
                color:        copied ? "#0b0d13" : "#FFD700",
                background:   copied ? "linear-gradient(135deg, #00FFCC 0%, #FFD700 100%)" : "#111118",
                border:       `1px solid ${copied ? "#00FFCC" : "#FFD700"}`,
                borderRadius: 8,
                cursor:       "pointer",
              }}
            >
              {copied ? "✓ Link copied" : "📻 Share this player"}
            </button>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/radio/rss`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="radio-rss-link"
              className="font-mono uppercase tracking-[0.22em] nx-gold-link"
              style={{
                padding:      "10px 16px",
                fontSize:     12,
                color:        "#00FFCC",
                background:   "#111118",
                border:       "1px solid #00FFCC",
                borderRadius: 8,
                textDecoration: "none",
              }}
            >
              ⧉ Podcast RSS Feed
            </a>
            <a
              href="/broadcasts"
              data-testid="radio-archive-link"
              className="font-mono uppercase tracking-[0.22em] nx-gold-link"
              style={{
                padding:      "10px 16px",
                fontSize:     12,
                color:        "#00FFCC",
                background:   "#111118",
                border:       "1px solid #00FFCC",
                borderRadius: 8,
                textDecoration: "none",
              }}
            >
              ◉ Broadcast Archive
            </a>
            <button
              type="button"
              data-testid="radio-copy-embed-btn"
              onClick={onCopyEmbed}
              className="font-mono uppercase tracking-[0.22em] nx-gold-link"
              style={{
                padding:      "10px 16px",
                fontSize:     12,
                color:        embedCopied ? "#0a0a0f" : "#FFD700",
                background:   embedCopied ? "#FFD700" : "#111118",
                border:       "1px solid #FFD700",
                borderRadius: 8,
                cursor:       "pointer",
              }}
            >
              {embedCopied ? "✓ Embed Code Copied" : "⧉ Copy Embed Code"}
            </button>
            <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "rgba(224,224,224,0.55)" }}>
              Dark theme · MP3 stream
            </span>
          </div>
        </section>

        {/* ── REX COMPANION · visual placeholder, wired to Emery later ── */}
        <section
          data-testid="radio-rex-companion"
          aria-label="Rex — Radio Companion"
          className="mt-12 mx-auto backdrop-blur-md"
          style={{
            background:       "#111118",
            border:           "1px solid rgba(0,255,204,0.45)",
            borderRadius:     22,
            boxShadow:        "0 0 32px rgba(0,255,204,0.12), inset 0 1px 0 rgba(255,255,255,0.05)",
            backdropFilter:   "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            padding:          24,
            maxWidth:         820,
          }}
        >
          <div
            className="flex items-center gap-3 mb-5"
            style={{ borderBottom: "1px dashed rgba(0,255,204,0.25)", paddingBottom: 12 }}
          >
            <img
              src={REX_FACE_URL}
              alt={REX_ALT}
              width={40}
              height={40}
              loading="lazy"
              style={{
                width: 40, height: 40, borderRadius: "50%", objectFit: "cover",
                border: "1px solid #FFD700", boxShadow: "0 0 12px rgba(255,215,0,0.35)",
              }}
            />
            <div>
              <p className="font-mono uppercase tracking-[0.24em]" style={{ fontSize: 12, color: "#00FFCC" }}>
                Rex · Radio Companion
              </p>
              <p className="font-mono uppercase tracking-[0.18em]" style={{ fontSize: 9, color: "rgba(224,224,224,0.5)" }}>
                Core Persona · Standing by on the Sovereign Pulse
              </p>
            </div>
            <span
              className="ml-auto font-mono uppercase tracking-[0.20em]"
              style={{ fontSize: 9, color: "#FFD700", border: "1px solid rgba(255,215,0,0.4)", borderRadius: 999, padding: "3px 10px" }}
            >
              Emery link · Pending
            </span>
          </div>

          <div className="space-y-4" data-testid="radio-rex-messages">
            {REX_MESSAGES.map((msg, i) => (
              <div key={i} className="flex items-start gap-3">
                <span
                  aria-hidden="true"
                  style={{
                    width: 8, height: 8, marginTop: 8, flexShrink: 0, borderRadius: "50%",
                    background: "#FFD700", boxShadow: "0 0 8px rgba(255,215,0,0.6)",
                  }}
                />
                <p
                  className="font-serif"
                  style={{
                    fontSize: 15, lineHeight: 1.65, color: "#e0e0e0",
                    background: "rgba(255,215,0,0.05)",
                    border: "1px solid rgba(255,215,0,0.18)",
                    borderRadius: "4px 14px 14px 14px",
                    padding: "10px 16px",
                    maxWidth: 620,
                  }}
                >
                  {msg}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center gap-3">
            <input
              type="text"
              data-testid="radio-rex-input"
              placeholder="Speak to Rex — channel opens when the Emery link goes live…"
              disabled
              aria-disabled="true"
              aria-label="Message Rex (channel not yet open)"
              className="flex-1 font-mono"
              style={{
                fontSize: 13,
                padding: "12px 16px",
                background: "rgba(4,5,10,0.6)",
                border: "1px solid rgba(0,255,204,0.3)",
                borderRadius: 10,
                color: "rgba(224,224,224,0.6)",
                cursor: "not-allowed",
              }}
            />
            <button
              type="button"
              data-testid="radio-rex-send-btn"
              disabled
              aria-disabled="true"
              className="font-mono uppercase tracking-[0.22em]"
              style={{
                padding: "12px 18px",
                fontSize: 11,
                color: "rgba(255,215,0,0.5)",
                background: "#111118",
                border: "1px solid rgba(255,215,0,0.35)",
                borderRadius: 10,
                cursor: "not-allowed",
              }}
            >
              Send
            </button>
          </div>
          <p className="mt-3 font-mono uppercase tracking-[0.2em]" style={{ fontSize: 9, color: "rgba(224,224,224,0.4)" }}>
            Visual console · Live channel arrives with the Emery uplink
          </p>
        </section>

        {/* ── Sub-caption ────────────────────────────────────── */}
        <p
          className="mt-8 font-mono text-center"
          style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: "rgba(224,224,224,0.5)" }}
        >
          NextXus Radio &middot; a16924 &middot; HumanCodex v5.1 &middot; 200-year mandate
        </p>
      </div>
    </main>
  );
}
